import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  CacheExpiryConstants,
  CacheKeyConstants,
  CacheService,
  FareCalculationNotFoundException,
  ReservationStatusNameConstants,
  ServiceException,
  TimeZoneUtil,
  UnauthorizedException,
  isNullOrUndefined
} from '@uzurv/core';
import { AuthDataService, UZURV_REST_CONFIGURATION, UzurvRestConfiguration, UzurvRestService } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { Observable, of, throwError, timer } from 'rxjs';
import { catchError, filter, map, mergeMapTo } from 'rxjs/operators';
import {
  BillingSummary,
  CancellationReason,
  ChatMessage,
  DriverEarnings,
  DriverEta,
  FareAdjustment,
  FareReviewDetail,
  FareReviewItem,
  FareReviewStatusUpdate,
  InsurancePolicy,
  Itinerary,
  MiniDriver,
  RecoveryRide,
  Reservation,
  ReservationDetail,
  ReservationRouteSummary,
  ReservationStatus,
  ReservationStatusUpdate,
  RideRequestLocation,
  RiderFare,
  RiderProtectedReservationDetail,
  Trip,
  Vehicle
} from '../../models';

@Injectable({
  providedIn: 'root'
})
export class ReservationService extends UzurvRestService {
  readonly resourceUrl = 'reservations/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer,
    private cacheService: CacheService,
    private authDataService: AuthDataService
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Returns a list of eligible vehicles for the reservation
   *  @param reservationId
   */

  getEligibleVehiclesForReservationById(reservationId: number): Observable<Array<Vehicle>> {
    return this.get<Array<Vehicle>>(`${this.resourceUrl}${reservationId}/eligibleVehicles/`).pipe(
      map(response => Deserialize(response, Vehicle))
    );
  }

  /**
   * Returns an updated Reservation
   *  @param reservationId
   *  @param vehicleId
   */

  updateReservationVehicle(reservationId: number, vehicleId: number): Observable<Reservation> {
    return this.patch<Reservation>(`${this.resourceUrl}${reservationId}/?vehicleId=${vehicleId}`, { vehicleId }).pipe(
      map(response => Deserialize(response, Reservation))
    );
  }

  /**
   * Returns a list of reservations
   *
   * @param pageNumber
   * @param pageSize
   * @param cancelled
   * @permission ride_monitoring
   */
  getReservations(pageNumber: number, pageSize: number, cancelled: boolean = false): Observable<Array<Trip>> {
    return this.get<Array<Trip>>(this.resourceUrl, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      cancelled: cancelled
    }).pipe(map(response => Deserialize(response, Trip)));
  }

  /**
   * Returns a reservation by ID
   *
   * @param reservationId
   * @permission reservation_read
   */
  findReservationById(reservationId: number): Observable<ReservationDetail> {
    return this.get<ReservationDetail>(`${this.resourceUrl}${reservationId}`).pipe(
      map(response => Deserialize(response, ReservationDetail))
    );
  }

  /**
   * Returns a page of reservation results with the given status. The other params are optional
   *
   * @param pageNumber
   * @param pageSize
   * @param reservationStatusIds
   * @param transitProgramIds
   * @param vehicleTypeIds
   * @permission ride_monitoring
   */
  listReservationsByStatusAndVehicleTypeAndTransitProgram(
    pageNumber: number,
    pageSize: number,
    reservationStatusIds: number | Array<number>,
    transitProgramIds?: number | Array<number>,
    vehicleTypeIds?: number | Array<number>
  ): Observable<Array<Trip>> {
    return this.get<Array<Trip>>('reservations/', {
      pageNumber: pageNumber,
      pageSize: pageSize,
      reservationStatusIds: reservationStatusIds,
      transitProgramIds: transitProgramIds,
      vehicleTypeIds: vehicleTypeIds
    }).pipe(map(response => Deserialize(response, Trip)));
  }

  /**
   * TODO(btate): move to rider controller?
   * Returns a list of reservations by rider ID
   *
   * @param riderId
   * @param pageNumber
   * @param pageSize
   * @param inactive
   */
  getReservationsByRiderId(
    riderId: number,
    pageNumber: number,
    pageSize: number,
    inactive: boolean = null
  ): Observable<Array<Reservation>> {
    const params = {
      pageNumber: pageNumber,
      pageSize: pageSize
    };

    if (!isNullOrUndefined(inactive)) {
      params['inactive'] = inactive;
    }

    return this.get<Array<Reservation>>('riders/' + riderId + '/reservations/', params).pipe(
      map(response => Deserialize(response, Reservation))
    );
  }

  /**
   * @deprecated
   * Returns a list of reservations by rider ID with options
   *
   * @param riderId
   * @param pageNumber
   * @param pageSize
   * @param parameters
   */
  getAllReservationsByRiderId(
    riderId: number,
    pageNumber: number,
    pageSize: number,
    parameters = {}
  ): Observable<Array<ReservationDetail>> {
    return this.get<Array<ReservationDetail>>('riders/' + riderId + '/reservations/', {
      ...parameters,
      pageNumber: pageNumber,
      pageSize: pageSize
    }).pipe(map(response => Deserialize(response, ReservationDetail)));
  }

  /**
   * @param reservation
   * @param interestedDriverId
   * @permission ride_monitoring
   */
  createReservationWithProposedPickupTime(reservation: Reservation, interestedDriverId: number): Observable<Reservation> {
    return this.post<Reservation>(this.resourceUrl, Serialize(reservation, Reservation), {
      params: { interestedDriverId: interestedDriverId }
    }).pipe(map(response => Deserialize(response, Reservation)));
  }

  /**
   * @param reservation
   * @permission ride_monitoring
   */
  create(reservation: Reservation): Observable<Reservation> {
    return this.post<Reservation>(this.resourceUrl, Serialize(reservation, Reservation)).pipe(
      map(response => Deserialize(response, Reservation))
    );
  }

  /**
   * Makes a request to adjust the fare calculations, returns the adjusted properties on success
   *
   * @param fareAdjustment Fare adjustment parameters as a FareAdjustment object
   */
  createFareAdjustmentForReservation(fareAdjustment: FareAdjustment): Observable<BillingSummary> {
    return this.put<BillingSummary>(`${this.resourceUrl}${fareAdjustment.reservationId}/fareAdjustment`, Serialize(fareAdjustment)).pipe(
      map(response => Deserialize(response, BillingSummary))
    );
  }

  /**
   * Cancels a reservation with a cancellation reason
   *
   * @param reservationId
   * @param cancellationReason
   * @permission ride_cancel
   */
  cancelReservationWithCancellationReason(reservationId: number, cancellationReason: CancellationReason): Observable<Reservation> {
    const patchDictionary = {
      id: reservationId,
      cancelled: true,
      cancellationReason: cancellationReason
    };

    return this.patch<Reservation>(`${this.resourceUrl}${reservationId}`, patchDictionary).pipe(
      map(response => Deserialize(response, Reservation))
    );
  }

  /**
   * Cancels a reservation and creates a recovery ride. Returns the created recovery itinerary if successful
   *
   * @param reservationId
   * @param cancellationReason
   * @permission ride_cancel
   */
  cancelReservationAndCreateRecoveryRide(
    reservationId: number,
    cancellationReason: CancellationReason,
    recoveryPickupLocation: RideRequestLocation,
    note: string
  ): Observable<Itinerary> {
    const recoveryRide: RecoveryRide = new RecoveryRide();

    recoveryRide.reservation = new Reservation();
    recoveryRide.reservation.id = reservationId;
    recoveryRide.reservation.cancelled = true;
    recoveryRide.reservation.cancellationReason = cancellationReason;

    recoveryRide.recoveryPickupLocation = recoveryPickupLocation;
    recoveryRide.note = note;

    return this.post<Itinerary>(`${this.resourceUrl}${reservationId}/createRecoveryRide/`, recoveryRide).pipe(
      map(response => Deserialize(response, Itinerary))
    );
  }

  /**
   * Cancels a reservation with a cancellation reason for a Web Self Service specifc user.
   *
   * @param reservationId
   * @param cancellationReason
   * @permission web_self_service_ride_cancel
   */
  cancelReservationWithCancellationReasonWebSelfServiceCancel(
    reservationId: number,
    cancellationReason: CancellationReason
  ): Observable<Reservation> {
    const patchDictionary = {
      id: reservationId,
      cancelled: true,
      cancellationReason: cancellationReason
    };

    return this.patch<Reservation>(`${this.resourceUrl}${reservationId}/webSelfServiceCancel/`, patchDictionary).pipe(
      map(response => Deserialize(response, Reservation))
    );
  }

  /**
   * Returns a list of reservation statuses
   *
   * @param overrideCache
   * @permission reservation_read
   */
  getReservationStatuses(overrideCache = false): Observable<Array<ReservationStatus>> {
    const cachedData = this.cacheService.getCachedData(CacheKeyConstants.RESERVATION_STATUSES);
    if (!overrideCache && !isNullOrUndefined(cachedData)) {
      return of(Deserialize(JSON.parse(cachedData), ReservationStatus));
    }

    return this.get<Array<ReservationStatus>>(`${this.resourceUrl}reservationStatuses/`).pipe(
      map(response => {
        this.cacheService.cacheData(
          CacheKeyConstants.RESERVATION_STATUSES,
          JSON.stringify(response),
          CacheExpiryConstants.DOMAIN_CACHE_EXPIRY
        );
        return Deserialize(response, ReservationStatus);
      })
    );
  }

  /**
   * Returns a reservation status by ID
   *
   * @param id
   * @param overrideCache
   */
  findReservationStatusWithId(id: number, overrideCache = false): Observable<ReservationStatus> {
    return this.getReservationStatuses(overrideCache).pipe(
      map(response => response.find(reservationStatus => reservationStatus.id === id))
    );
  }

  /**
   * Returns a reservation status by name
   *
   * @param name
   * @param overrideCache
   */
  findReservationStatusWithName(name: string, overrideCache = false): Observable<ReservationStatus> {
    return this.getReservationStatuses(overrideCache).pipe(
      map(response => response.find(reservationStatus => reservationStatus.name === name))
    );
  }

  /**
   * Returns a reservation route summary by reservation ID
   *
   * @param reservationId
   * @param routePeriodCode
   * @permission reservation_read
   */
  findReservationRouteSummaryByReservationId(reservationId: number, routePeriodCode: string): Observable<ReservationRouteSummary> {
    return this.get<ReservationRouteSummary>(`${this.resourceUrl}${reservationId}/reservationRouteSummary/`, {
      routePeriodCode: routePeriodCode
    }).pipe(map(response => Deserialize(response, ReservationRouteSummary)));
  }

  /**
   * Returns driver earsnings by reservation ID
   *
   * @param reservationId
   */
  findDriverEarningsByReservationId(reservationId: number): Observable<DriverEarnings> {
    return this.get<DriverEarnings>(`${this.resourceUrl}${reservationId}/driverEarnings/`)
      .pipe(map(response => Deserialize(response, DriverEarnings)))
      .pipe(
        catchError(err => {
          const message: string = isNullOrUndefined(err.message) ? '' : err.message;

          if (message.includes('FareCalculation for Reservation with id') && message.includes('not found')) {
            return throwError(new FareCalculationNotFoundException(message));
          }

          return throwError(err);
        })
      );
  }

  /**
   * Returns a list of cancellation reasons, optionally overriding the cached values
   *
   * @param overrideCache
   */
  listReservationCancellationReasons(): Observable<Array<CancellationReason>> {
    return this.get<Array<CancellationReason>>(`${this.resourceUrl}cancellationReasons/`).pipe(
      map(response => Deserialize(response, CancellationReason))
    );
  }

  /**
   * Gets the driver's estimated time of arrival for a given reservation
   *
   * @param reservationId
   * @permission reservation_read
   */
  getDriverEta(reservationId: number): Observable<DriverEta> {
    return this.get<DriverEta>(`${this.resourceUrl}${reservationId}/estimatedTimeOfArrival`).pipe(
      map(response => Deserialize(response, DriverEta))
    );
  }

  /*****************  Chat Messages *********************/

  /**
   * Returns the chat message list attached to a given reservation
   *
   * @param reservationId
   * @permission chat_message_read
   */
  getChatMessagesForReservation(reservationId: number): Observable<Array<ChatMessage>> {
    return this.get('reservations/' + reservationId + '/chatMessages/').pipe(map(response => Deserialize(response, ChatMessage)));
  }

  /**
   * Sends a chat message from the current logged in user to a provided user by ID, and is
   * attached to a provided reservation by ID. Authentication data will be cleared if it
   * can't be found
   *
   * @param reservationId
   * @param recipientPersonId
   * @param message
   * @permission chat_message_create
   */
  sendChatMessage(reservationId: number, recipientPersonId: number, message: string): Observable<ChatMessage> {
    if (!this.authDataService.isLoggedIn) {
      throw new UnauthorizedException('Unauthorized');
    }

    const chatMessage: ChatMessage = new ChatMessage();

    chatMessage.text = message;
    chatMessage.recipientPersonId = recipientPersonId;
    chatMessage.senderPersonId = this.authDataService.currentUser.claraPersonId;
    chatMessage.reservationId = reservationId;

    return this.post('reservations/' + reservationId + '/chatMessages/', Serialize(chatMessage)).pipe(
      map(response => Deserialize(response, ChatMessage))
    );
  }

  /*****************  Fare Adjustment Review Queue *********************/

  /**
   * Returns a list of fare review items for processing
   *
   * @param pageNumber
   * @param pageSize
   * @param stateCode
   */
  listFareReviewItems(pageNumber: number, pageSize: number, stateCode?: string): Observable<Array<FareReviewItem>> {
    return this.get<Array<FareReviewItem>>(`${this.resourceUrl}fareReviews/`, {
      pageNumber: pageNumber,
      pageSize: pageSize,
      stateCode: stateCode
    }).pipe(map(response => Deserialize(response, FareReviewItem)));
  }

  /**
   * Returns a dry run billing summary for a given fare adjustment. Used to give the
   * user feedback on what effect their fare adjustment will have on the billing summary
   *
   * @param fareAdjustment
   * @permission fare_review
   */
  estimateFareAdjustment(fareAdjustment: FareAdjustment): Observable<BillingSummary> {
    return this.put<BillingSummary>(
      `${this.resourceUrl}${fareAdjustment.reservationId}/estimateFareAdjustment`,
      Serialize(fareAdjustment, FareAdjustment)
    ).pipe(map(response => Deserialize(response, BillingSummary)));
  }

  /**
   * @param reservationId
   * @permission fare_review
   */
  getFareReviewDetail(reservationId: number): Observable<FareReviewDetail> {
    return this.get<FareReviewDetail>(`${this.resourceUrl}${reservationId}/fareReviewDetail`).pipe(
      map(response => Deserialize(response, FareReviewDetail))
    );
  }

  /**
   * @param reservationId
   * @permission reservation_read
   */
  getRiderFaresByReservationId(reservationId: number): Observable<Array<RiderFare>> {
    return this.get<Array<RiderFare>>(`${this.resourceUrl}${reservationId}/riderFares/`).pipe(
      map(response => Deserialize(response, RiderFare))
    );
  }

  /*
   *
   */
  saveRiderFares(reservationId: number, riderFares: Array<RiderFare>): Observable<Array<RiderFare>> {
    return this.post<Array<RiderFare>>(`${this.resourceUrl}${reservationId}/riderFares/?multi=true`, Serialize(riderFares)).pipe(
      map(response => Deserialize(response, RiderFare))
    );
  }

  /**
   * Updates the status of a fare review item and returns the updated reservation
   *
   * Valid options are:
   *
   * `Completed, Adjusted` - Complete review with adjustement, FareAdjustmentRO CAN NOT be null.
   *
   * `Completed, Not Adjusted` -  Complete review without adjustments to calculated fare, FareAdjustmentRO MUST be null
   *
   * `In Progress` -  Can transition both to and from "Required". Viewing a fare review SHOULD transition the
   *    status to "In Progress". Canceling out of a review without completing it SHOULD transition
   *    back to to "Required".
   *
   * `Required` -  CAN transition to "In Progress" or directly to one of the Completed statuses.
   *
   * @param fareReviewStatusUpdate
   */
  updateFareReviewStatus(fareReviewStatusUpdate: FareReviewStatusUpdate): Observable<Reservation> {
    return this.put<Reservation>(
      `${this.resourceUrl}${fareReviewStatusUpdate.reservationId}/fareReviewStatus/`,
      Serialize(fareReviewStatusUpdate)
    ).pipe(map(response => Deserialize(response, Reservation)));
  }

  /**
   * Returns a cancellation reason by ID
   *
   * @param id
   * @param overrideCache
   */
  findCancellationReasonWithId(id: number): Observable<CancellationReason> {
    return this.listReservationCancellationReasons().pipe(
      map(response => response.find(cancellationReason => cancellationReason.id === id))
    );
  }

  /**
   * Returns a cancellation reason by name
   *
   * @param name
   * @param overrideCache
   */
  findCancellationReasonWithName(name: string): Observable<CancellationReason> {
    return this.listReservationCancellationReasons().pipe(
      map(response => response.find(cancellationReason => cancellationReason.name === name))
    );
  }

  /**
   * Gets the geocoordinate position of the driver attached to a reservaiton
   *
   * @param reservationId
   * @permission reservation_read
   */
  getDriverPosition(reservationId: number): Observable<MiniDriver> {
    return this.get<MiniDriver>(`${this.resourceUrl}${reservationId}/driverPosition`).pipe(
      map(response => Deserialize(response, MiniDriver))
    );
  }

  /**
   * Subscribes to a feed of driver location by reservation ID. Every {interval} seconds, the current
   * location of the driver is returned to the subscriber
   *
   * @param reservationId
   * @param interval the time in seconds between polling requests
   */
  trackDriverLocation(reservationId: number, interval?: number): Observable<MiniDriver> {
    return timer(0, interval).pipe(
      filter(() => this.authDataService.isLoggedIn),
      mergeMapTo(this.getDriverPosition(reservationId))
    );
  }

  /*****************  Insurance Policy *********************/

  /**
   * Returns the insurance policy applicable to a reservation
   *
   * @param reservationId
   * @permission ride_monitoring
   */
  findInsurancePolicyForReservation(reservationId: number): Observable<InsurancePolicy> {
    return this.get(`${this.resourceUrl}${reservationId}/insurancePolicy/`).pipe(
      map(response => {
        if (isNullOrUndefined(response)) {
          throw new ServiceException('Insurance Policy Not Found');
        }
        return Deserialize(response, InsurancePolicy);
      })
    );
  }

  /**
   * Gets a timezone adjusted pickup date string with the given format
   *
   * @param date
   * @param timeZone
   * @param dateFormat
   */
  getFormattedTimeZoneDate(date: Date, timeZone: string, dateFormat = 'M/DD/YY h:mm a (tz)'): string {
    return TimeZoneUtil.formatTimeZoneDate(date, timeZone, dateFormat);
  }

  /***************** Sorting ******************/

  /**
   * Returns a list of reservations sorted by pickup date, ascending or descending
   *
   * @param reservations
   * @param ascending
   */
  sortReservationsByPickupDate(reservations: Array<ReservationDetail>, ascending = true): Array<ReservationDetail> {
    if (isNullOrUndefined(reservations)) {
      return [];
    }

    return reservations.sort((a, b) => {
      let aPickupDate = a.rideRequestDetail.pickupDate;
      let bPickupDate = b.rideRequestDetail.pickupDate;

      if (isNullOrUndefined(aPickupDate)) {
        aPickupDate = new Date();
      }
      if (isNullOrUndefined(bPickupDate)) {
        bPickupDate = new Date();
      }

      if (ascending) {
        return aPickupDate.getTime() - bPickupDate.getTime();
      } else {
        return bPickupDate.getTime() - aPickupDate.getTime();
      }
    });
  }

  /**
   * Returns a list of protected reservations sorted by pickup date, ascending or descending
   *
   * @param reservations
   * @param ascending
   */
  sortProtectedReservationsByPickupDate(
    reservations: Array<RiderProtectedReservationDetail>,
    ascending = true
  ): Array<RiderProtectedReservationDetail> {
    if (isNullOrUndefined(reservations)) {
      return [];
    }

    return reservations.sort((a, b) => {
      let aPickupDate = a.protectedRideRequestDetail.pickupDate;
      let bPickupDate = b.protectedRideRequestDetail.pickupDate;

      if (isNullOrUndefined(aPickupDate)) {
        aPickupDate = new Date();
      }
      if (isNullOrUndefined(bPickupDate)) {
        bPickupDate = new Date();
      }

      if (ascending) {
        return aPickupDate.getTime() - bPickupDate.getTime();
      } else {
        return bPickupDate.getTime() - aPickupDate.getTime();
      }
    });
  }

  /********* Reservation Status ***************/

  /**
   * Updates a reservation with a new reservation status
   *
   * @param reservationId
   * @param reservationStatusName
   */
  updateReservationStatus(
    reservationId: number,
    reservationStatusName: string,
    timestamp: Date = new Date()
  ): Observable<ReservationDetail> {
    const reservationStatusUpdate: ReservationStatusUpdate = new ReservationStatusUpdate();
    reservationStatusUpdate.reservationId = reservationId;
    reservationStatusUpdate.reservationStatusName = reservationStatusName;
    reservationStatusUpdate.timestamp = timestamp;

    return this.put<RiderProtectedReservationDetail>(
      `${this.resourceUrl}${reservationId}/reservationStatus/`,
      Serialize(reservationStatusUpdate)
    ).pipe(map(response => Deserialize(response, ReservationDetail)));
  }

  /***************** Util *********************/

  isCompleted(reservationStatus: ReservationStatus): boolean {
    return reservationStatus.name === ReservationStatusNameConstants.COMPLETED;
  }
}
